import { t } from "i18next"

import { DisplayableError } from "@treefort/lib/displayable-error"

import analytics from "./analytics"
import {
  getTracksFromConsumableContent,
  VideoConsumableContent,
} from "./consumable-content"
import DownloadItem from "./download-item"
import { logError } from "./logging"
import { getNetworkState, isOfflineState } from "./network-state"
import {
  handlePlaybackRateChanged,
  handlePlaybackStateChanged,
  handlePlayProgressEvent,
  handleTracksChanged,
  PlayProgressTracker,
} from "./play-progress-tracking"
import { fetchProgressForConsumableContent } from "./progress-item"
import { Event, PlaybackState, Progress, VideoPlayer } from "./video-player"

/**
 * Adds listeners for changes in progress, playback rate, etc. for a particular
 * piece of content/progress item. Returns a function to remove the listeners.
 */
function addEventListeners(videoPlayer: VideoPlayer) {
  // Every time we setup these listeners on a new player that represents a
  // change in the currently playing tracks
  handleTracksChanged()

  const playProgressTracker = new PlayProgressTracker({
    playbackState: PlaybackState.Idle,
    playbackRate: videoPlayer.getPlaybackRate(),
  })

  videoPlayer.on(Event.Progress, ({ position, duration }: Progress) => {
    const track = videoPlayer.getTrack()
    handlePlayProgressEvent({
      track,
      tracks: track ? [track] : [],
      position,
      duration,
      playbackRate: videoPlayer.getPlaybackRate(),
      playMode: "watch",
      playProgressTracker,
    })
  })

  videoPlayer.on(Event.PlaybackRate, (playbackRate) =>
    handlePlaybackRateChanged({ playbackRate, playProgressTracker }),
  )

  videoPlayer.on(Event.PlaybackState, (playbackState) =>
    handlePlaybackStateChanged({ playbackState, playProgressTracker }),
  )
}

/**
 * This takes in a ConsumableContentVideo object and creates a VideoPlayer
 * instance that can be passed to the VideoPlayerInline component to render the
 * video.
 */
export async function playContentVideo({
  consumableContent,
  profileId,
}: {
  consumableContent: VideoConsumableContent
  profileId: string | null
}): Promise<VideoPlayer | undefined> {
  const videoPlayer = new VideoPlayer()
  try {
    // Initialize the player
    videoPlayer.publishPlayIntent()
    addEventListeners(videoPlayer)

    // Make sure we're either online or the video is downloaded
    const downloadItem = new DownloadItem({ consumableContent })
    const [networkState, downloadItemState] = await Promise.all([
      getNetworkState(),
      downloadItem.getState(),
    ])
    if (
      isOfflineState(networkState) &&
      downloadItemState.type !== "downloaded"
    ) {
      throw new DisplayableError(
        t("Cannot play video - no network connection."),
      )
    }

    // Make sure we were given a video to play
    const tracks = getTracksFromConsumableContent({
      consumableContent: await downloadItem.getOfflineConsumableContent(),
      profileId,
    })
    if (tracks.length === 0) {
      throw new DisplayableError(
        t("Please update your app to access this content."),
        `[Content] No compatible tracks found for content "${consumableContent.content.id}". Client is likely outdated.`,
      )
    }

    // Load progress
    const progressItem = await fetchProgressForConsumableContent({
      consumableContent,
      profileId,
    })

    // Start playback
    videoPlayer.setTrack(tracks[0], {
      initialPosition: progressItem.getProgress().finished
        ? 0
        : progressItem.getProgress().position,
      playbackRate: progressItem.getPlaybackRate(),
    })

    // Log a play request but don't block playback while we do so
    analytics.logPlayRequest({ tracks, progressItem })

    return videoPlayer
  } catch (error) {
    logError(
      error instanceof DisplayableError
        ? error
        : new DisplayableError(
            t("An error occurred. Please contact us if the issue persists."),
            error,
          ),
    )
  }
}
